import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import { INITIAL_LIQUIDATION_QUEUE, type LiquidationQueueEntry, type LiquidationWorkflowStatus } from '../data/liquidations';
import {
  FilterTabs,
  PaginationControls,
  SearchBar,
  SortableHeader,
  type SortDirection,
  usePaginatedRows,
  useTableControls,
} from './TableControls';

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium';
type LiquidationSortKey =
  | 'accountId'
  | 'facilityName'
  | 'wallet'
  | 'collateralAmount'
  | 'debtAmount'
  | 'healthFactor'
  | 'status'
  | 'timeInDefault';

const SEVERITY_FILTERS: { value: SeverityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
];

function formatCurrency(value: number): string {
  return `$${value.toLocaleString()}`;
}

function queueSeverity(healthFactor: number): Exclude<SeverityFilter, 'all'> {
  if (healthFactor < 0.9) return 'critical';
  if (healthFactor < 1.1) return 'high';
  return 'medium';
}

function resolutionClass(status: LiquidationWorkflowStatus): string {
  if (status === 'not_started') return 'bg-yellow-100 text-yellow-700';
  if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
  return 'bg-green-100 text-green-700';
}

function resolutionLabel(status: LiquidationWorkflowStatus, hasDdf: boolean): string {
  if (status === 'liquidated') return 'Liquidated';
  if (status === 'in_progress') return 'DDF Auction Active';
  return hasDdf ? 'DDF Eligible' : 'Monitoring';
}

function parseDurationToMinutes(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 0;
  if (value.endsWith('h')) return parsed * 60;
  if (value.endsWith('d')) return parsed * 60 * 24;
  return parsed;
}

export function LiquidationQueueTable({
  facilityName,
  title,
  description,
  initialRows = INITIAL_LIQUIDATION_QUEUE,
}: {
  facilityName?: string;
  title?: string;
  description?: string;
  initialRows?: LiquidationQueueEntry[];
}) {
  const navigate = useNavigate();
  const [entries] = useState(initialRows);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [sortKey, setSortKey] = useState<LiquidationSortKey>('healthFactor');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { query, setQuery, page, setPage, pageSize, setPageSize, resetPage } = useTableControls();

  const queueRows = useMemo(() => {
    let rows = entries.filter((item) => item.status !== 'liquidated' && item.debtAmount > 0);
    if (facilityName) rows = rows.filter((item) => item.facilityName === facilityName);
    return rows;
  }, [entries, facilityName]);

  const filteredRows = useMemo(() => {
    let rows = queueRows;

    if (severityFilter !== 'all') {
      rows = rows.filter((item) => queueSeverity(item.healthFactor) === severityFilter);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter(
        (item) =>
          item.accountId.toLowerCase().includes(q) ||
          item.wallet.toLowerCase().includes(q) ||
          item.facilityName.toLowerCase().includes(q),
      );
    }

    return rows;
  }, [queueRows, severityFilter, query]);

  const counts = useMemo(
    () => ({
      all: queueRows.length,
      critical: queueRows.filter((item) => queueSeverity(item.healthFactor) === 'critical').length,
      high: queueRows.filter((item) => queueSeverity(item.healthFactor) === 'high').length,
      medium: queueRows.filter((item) => queueSeverity(item.healthFactor) === 'medium').length,
    }),
    [queueRows],
  );

  const sortedRows = useMemo(() => {
    const statusOrder: Record<LiquidationWorkflowStatus, number> = {
      not_started: 0,
      in_progress: 1,
      liquidated: 2,
    };

    return [...filteredRows].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'accountId':
          comparison = a.accountId.localeCompare(b.accountId);
          break;
        case 'facilityName':
          comparison = a.facilityName.localeCompare(b.facilityName);
          break;
        case 'wallet':
          comparison = a.wallet.localeCompare(b.wallet);
          break;
        case 'collateralAmount':
          comparison = a.collateralAmount - b.collateralAmount;
          break;
        case 'debtAmount':
          comparison = a.debtAmount - b.debtAmount;
          break;
        case 'healthFactor':
          comparison = a.healthFactor - b.healthFactor;
          break;
        case 'status':
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
        case 'timeInDefault':
          comparison = parseDurationToMinutes(a.timeInDefault) - parseDurationToMinutes(b.timeInDefault);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortDirection, sortKey]);

  const { rows: paginatedRows } = usePaginatedRows(sortedRows, page, pageSize);

  function onSort(nextKey: LiquidationSortKey) {
    setSortDirection((prevDirection) =>
      sortKey === nextKey ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc',
    );
    setSortKey(nextKey);
    resetPage();
  }

  const showFacilityColumn = !facilityName;

  return (
    <div className="rounded-xl border border-border p-5 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">{title ?? 'Positions at Risk'}</h3>
          <p className="text-sm text-muted-foreground">{description ?? 'Monitor undercollateralized positions across all facilities.'}</p>
        </div>
        <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {queueRows.length} Positions
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs<SeverityFilter>
          filters={SEVERITY_FILTERS}
          active={severityFilter}
          counts={counts}
          onChange={(value) => {
            setSeverityFilter(value);
            resetPage();
          }}
        />
        <SearchBar
          value={query}
          onChange={(value) => {
            setQuery(value);
            resetPage();
          }}
          placeholder="Search by account, wallet, or facility..."
          className="w-full sm:w-[420px]"
        />
      </div>

      {sortedRows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
          <Icon name="streamline:warning-triangle" className="mx-auto h-8 w-8 text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground font-medium">No positions currently at risk.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left">
                  <SortableHeader<LiquidationSortKey>
                    label="Account"
                    sortKey="accountId"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                  />
                  {showFacilityColumn && (
                    <SortableHeader<LiquidationSortKey>
                      label="Facility"
                      sortKey="facilityName"
                      activeSortKey={sortKey}
                      direction={sortDirection}
                      onSort={onSort}
                    />
                  )}
                  <SortableHeader<LiquidationSortKey>
                    label="Wallet"
                    sortKey="wallet"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                  />
                  <SortableHeader<LiquidationSortKey>
                    label="Collateral"
                    sortKey="collateralAmount"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                    align="right"
                  />
                  <SortableHeader<LiquidationSortKey>
                    label="Debt"
                    sortKey="debtAmount"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                    align="right"
                  />
                  <SortableHeader<LiquidationSortKey>
                    label="HF"
                    sortKey="healthFactor"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                    align="right"
                  />
                  <SortableHeader<LiquidationSortKey>
                    label="Resolution"
                    sortKey="status"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                  />
                  <SortableHeader<LiquidationSortKey>
                    label="Time in Default"
                    sortKey="timeInDefault"
                    activeSortKey={sortKey}
                    direction={sortDirection}
                    onSort={onSort}
                  />
                  <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((item) => (
                  <tr key={item.id} onClick={() => navigate(`/margin-accounts/${item.accountId}`)} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer">
                    <td className="px-3 py-2.5 font-mono text-xs">{item.accountId}</td>
                    {showFacilityColumn && <td className="px-3 py-2.5">{item.facilityName}</td>}
                    <td className="px-3 py-2.5 font-mono text-xs">{item.wallet}</td>
                    <td className="px-3 py-2.5 text-right">{formatCurrency(item.collateralAmount)}</td>
                    <td className="px-3 py-2.5 text-right">{formatCurrency(item.debtAmount)}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold ${item.healthFactor < 1 ? 'text-red-600' : item.healthFactor < 1.1 ? 'text-orange-600' : 'text-yellow-700'}`}>
                      {item.healthFactor.toFixed(2)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${resolutionClass(item.status)}`}>
                        {resolutionLabel(item.status, item.hasDdf)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">{item.timeInDefault}</td>
                    <td className="px-3 py-2.5">
                      {item.hasDdf && item.status === 'not_started' ? (
                        <span className="text-xs text-muted-foreground">Awaiting searcher</span>
                      ) : item.hasDdf && item.status === 'in_progress' ? (
                        <span className="text-xs text-muted-foreground">DDF Active</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationControls
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={sortedRows.length}
            label="positions"
          />
        </>
      )}
    </div>
  );
}
